import { BlogNavbar, Layout } from '@msanvarov/core-components';
import {
  Post,
  setCategories,
  setPosts,
  setTags,
  useAppDispatch,
} from '@msanvarov/store';
import { DiscussionEmbed } from 'disqus-react';
import fs from 'fs';
import matter from 'gray-matter';
import moment from 'moment';
import { GetStaticPaths, GetStaticProps } from 'next';
import { MDXRemote, MDXRemoteSerializeResult } from 'next-mdx-remote';
import { serialize } from 'next-mdx-remote/serialize';
import Link from 'next/link';
import { useRouter } from 'next/router';
import path from 'path';
import remarkGfm from 'remark-gfm';
import { POSTS_PATH, postFilePaths } from '../../utils/mdx.utils';
import { useEffect, useState } from 'react';
import { Button, Menu, MenuItem } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = postFilePaths
    .map((path) => path.replace(/\.mdx?$/, ''))
    .map((post) => ({ params: { post } }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<PostPageProps> = async ({
  params,
}) => {
  const posts = postFilePaths
    .map((filePath) => {
      const fileContents = fs.readFileSync(path.join(POSTS_PATH, filePath));
      const { content, data } = matter(fileContents);

      return {
        content,
        metadata: data,
        filePath,
      };
    })
    .sort(
      (a, b) =>
        moment(b.metadata.created).unix() - moment(a.metadata.created).unix()
    );

  const postFilePath = path.join(POSTS_PATH, `${params?.post}.mdx`);
  const source = fs.readFileSync(postFilePath);

  const { content, data } = matter(source);

  const markdownContent = await serialize(content, {
    mdxOptions: {
      remarkPlugins: [remarkGfm],
      rehypePlugins: [],
    },
    scope: data,
  });

  return {
    props: {
      content: markdownContent,
      frontMatter: data,
      posts,
    },
  };
};

const components = {};

type PostPageProps = {
  content: MDXRemoteSerializeResult<
    Record<string, unknown>,
    Record<string, unknown>
  >;
  frontMatter: Record<string, string>;
  posts: Post[];
};

const PostPage = ({ content, frontMatter, posts }: PostPageProps) => {
  const { asPath } = useRouter();

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const dispatch = useAppDispatch();

  const filteredPosts = posts.filter((post) =>
    post.metadata.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleClick = (event: any, category: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedCategory(category);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const origin =
    typeof window !== 'undefined' && window.location.origin
      ? window.location.origin
      : '';

  const url = `${origin}${asPath}`;

  const categories = Array.from(
    new Set(posts.map((post) => post.metadata.category))
  );

  const tags = Array.from(new Set(posts.map((post) => post.metadata.tag)));

  useEffect(() => {
    if (posts) {
      dispatch(setPosts(posts));
      dispatch(setCategories(categories));
      dispatch(setTags(tags));
    }
  }, [posts, categories, tags]);

  return (
      <section className="blog-details-area">
        <div className="container">
          <div
            className="navigator"
            style={{
              backgroundColor: '#F6F7F8',
              padding: '10px',
              borderRadius: '20px',
              textAlign: 'center',
              marginBottom: '30px',
              position: 'relative',
              minHeight: '20px',
              height: 'auto',
           
            }}
          >
            {categories.map((category, index) => (
              <div
                key={index}
                style={{ display: 'inline-block', margin: '0 30px' }}
              >
                <Button
                  aria-controls={`menu-${index}`}
                  aria-haspopup="true"
                  onClick={(e) => handleClick(e, category)}
                  endIcon={<ArrowDropDownIcon />}
                  sx={{ color: '#363636', fontWeight: 'bold' }}
                >
                  {category}
                </Button>
                <Menu
                  id={`menu-${index}`}
                  anchorEl={anchorEl}
                  keepMounted
                  open={Boolean(anchorEl) && selectedCategory === category}
                  onClose={handleClose}
                  PaperProps={{
                    style: {
                      background: 'rgba(255, 255, 255, 0.89)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: '20px',
                      boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
                    },
                  }}
                >
                  {posts
                    .filter((post) => post.metadata.category === category)
                    .map((post, i) => (
                      <MenuItem key={i} style={{ padding: '10px 20px' }}>
                        <Link
                          as={`/posts/${post.filePath.replace(/\.mdx?$/, '')}`}
                          href={`/posts/[post]`}
                          style={{
                            textDecoration: 'none',
                            color: '#363636',
                            display: 'block',
                            width: '100%',
                          }}
                        >
                          {post.metadata.title}
                        </Link>
                      </MenuItem>
                    ))}
                </Menu>
              </div>
            ))}
          </div>
          <div className="row">
            <div className="col-md-8">
              <div className="blog-details-content">
                <div className="img-box">
                  <img src={frontMatter.thumbnail} alt="Blog" />
                </div>
                <span className="meta">{frontMatter.category}</span>
                <h1>{frontMatter.title}</h1>
                <MDXRemote {...content} components={components} />
                <div className="tags">
                  <Link href="#" className="theme-btn my-4">
                    {frontMatter.tag}
                  </Link>
                </div>

                {/* Google Ads */}
                <div>
                  <ins
                    className="adsbygoogle"
                    style={{ display: 'block', textAlign: 'center' }}
                    data-ad-layout="in-article"
                    data-ad-format="fluid"
                    data-ad-client="ca-pub-9965905819710501"
                    data-ad-slot="6927771041"
                  ></ins>
                  <script
                    dangerouslySetInnerHTML={{
                      __html: `
                      (adsbygoogle = window.adsbygoogle || []).push({});
                    `,
                    }}
                  />
                </div>

                <DiscussionEmbed
                  shortname="sal-anvarov"
                  config={{
                    url,
                    identifier: frontMatter.uid,
                    title: frontMatter.title,
                  }}
                />
              </div>
            </div>
            <div className="col-md-4">
              <BlogNavbar posts={posts} categories={categories} tags={tags} />
            </div>
          </div>
        </div>
      </section>
  );
};

export default PostPage;
